import type { FieldDefinition } from '../../../data/model';
import {
  addAttributeRuleChild,
  attributeRuleConditionWithField,
  attributeRuleConditionWithOperator,
  countAttributeRuleConditions,
  createDefaultAttributeRuleCondition,
  createDefaultAttributeRuleGroup,
  removeAttributeRuleAtPath,
  updateAttributeRuleAtPath,
  type AttributeRulePath,
} from '../../../rules/editing';
import { attributeRuleField, attributeRuleFields } from '../../../rules/fields';
import type {
  AttributeRule,
  AttributeRuleCondition,
  AttributeRuleGroup,
  AttributeRuleOperator,
} from '../../../rules/model';
import {
  attributeRuleOperatorDefinition,
  attributeRuleOperatorsForField,
} from '../../../rules/operators';
import { MAX_ATTRIBUTE_RULE_CHILDREN, MAX_ATTRIBUTE_RULE_DEPTH } from '../../../rules/validation';
import type { LayerId } from '../../../project/model';
import type { LabelChangeResult } from '../../state/appStore';
import { useAppStore } from '../../state/useAppStore';
import { fieldDisplayLabel } from '../../../joins/effectiveLayer';

interface LabelFilterEditorProps {
  readonly fields: readonly FieldDefinition[];
  readonly filter: AttributeRuleGroup | null;
  readonly layerId: LayerId;
  readonly onResult: (result: LabelChangeResult) => void;
}

interface RuleGroupEditorProps {
  readonly fields: readonly FieldDefinition[];
  readonly group: AttributeRuleGroup;
  readonly onAddCondition: (path: AttributeRulePath) => void;
  readonly onAddGroup: (path: AttributeRulePath) => void;
  readonly onChangeCondition: (path: AttributeRulePath, condition: AttributeRuleCondition) => void;
  readonly onChangeGroup: (path: AttributeRulePath, group: AttributeRuleGroup) => void;
  readonly onRemove: (path: AttributeRulePath) => void;
  readonly path: AttributeRulePath;
}

function pathName(path: AttributeRulePath): string {
  return path.length === 0 ? 'root' : path.map((index) => index + 1).join('.');
}

function ConditionEditor({
  condition,
  fields,
  onChange,
  onRemove,
  path,
}: {
  readonly condition: AttributeRuleCondition;
  readonly fields: readonly FieldDefinition[];
  readonly onChange: (condition: AttributeRuleCondition) => void;
  readonly onRemove: () => void;
  readonly path: AttributeRulePath;
}) {
  const field = attributeRuleField(fields, condition.field);
  if (!field) return null;
  const operators = attributeRuleOperatorsForField(field);
  const definition = attributeRuleOperatorDefinition(field, condition.operator);
  const name = pathName(path);

  return (
    <div className="label-filter-condition">
      <select
        aria-label={`Filter field ${name}`}
        onChange={(event) => {
          const next = attributeRuleConditionWithField(condition, event.target.value, fields);
          if (next) onChange(next);
        }}
        value={condition.field}
      >
        {attributeRuleFields(fields).map((candidate) => (
          <option key={candidate.name} value={candidate.name}>
            {fieldDisplayLabel(candidate)}
          </option>
        ))}
      </select>
      <select
        aria-label={`Filter operator ${name}`}
        onChange={(event) => {
          const next = attributeRuleConditionWithOperator(
            condition,
            event.target.value as AttributeRuleOperator,
            fields,
          );
          if (next) onChange(next);
        }}
        value={condition.operator}
      >
        {operators.map((operator) => (
          <option key={operator.operator} value={operator.operator}>
            {operator.label}
          </option>
        ))}
      </select>
      {definition?.valueKind === 'number' ? (
        <input
          aria-label={`Filter value ${name}`}
          onChange={(event) => {
            const value = Number(event.target.value);
            if (Number.isFinite(value)) onChange({ ...condition, value });
          }}
          type="number"
          value={typeof condition.value === 'number' ? condition.value : 0}
        />
      ) : definition?.valueKind === 'string' ? (
        <input
          aria-label={`Filter value ${name}`}
          onChange={(event) => onChange({ ...condition, value: event.target.value })}
          type="text"
          value={typeof condition.value === 'string' ? condition.value : ''}
        />
      ) : null}
      <button aria-label={`Remove condition ${name}`} onClick={onRemove} type="button">
        ×
      </button>
    </div>
  );
}

function RuleGroupEditor({
  fields,
  group,
  onAddCondition,
  onAddGroup,
  onChangeCondition,
  onChangeGroup,
  onRemove,
  path,
}: RuleGroupEditorProps) {
  const name = pathName(path);
  return (
    <div className={path.length === 0 ? 'label-filter-group' : 'label-filter-group nested'}>
      <div className="label-filter-group-header">
        <span>Match</span>
        <select
          aria-label={`Match conditions ${name}`}
          onChange={(event) =>
            onChangeGroup(path, {
              ...group,
              operator: event.target.value === 'or' ? 'or' : 'and',
            })
          }
          value={group.operator}
        >
          <option value="and">all</option>
          <option value="or">any</option>
        </select>
        <span>conditions</span>
        {path.length > 0 ? (
          <button aria-label={`Remove group ${name}`} onClick={() => onRemove(path)} type="button">
            Remove group
          </button>
        ) : null}
      </div>
      <div className="label-filter-children">
        {group.children.map((child, index) => {
          const childPath = [...path, index];
          return child.kind === 'condition' ? (
            <ConditionEditor
              condition={child}
              fields={fields}
              key={pathName(childPath)}
              onChange={(condition) => onChangeCondition(childPath, condition)}
              onRemove={() => onRemove(childPath)}
              path={childPath}
            />
          ) : (
            <RuleGroupEditor
              fields={fields}
              group={child}
              key={pathName(childPath)}
              onAddCondition={onAddCondition}
              onAddGroup={onAddGroup}
              onChangeCondition={onChangeCondition}
              onChangeGroup={onChangeGroup}
              onRemove={onRemove}
              path={childPath}
            />
          );
        })}
      </div>
      <div className="label-filter-actions">
        <button
          disabled={group.children.length >= MAX_ATTRIBUTE_RULE_CHILDREN}
          onClick={() => onAddCondition(path)}
          type="button"
        >
          Add condition
        </button>
        {path.length < MAX_ATTRIBUTE_RULE_DEPTH - 1 ? (
          <button
            disabled={group.children.length >= MAX_ATTRIBUTE_RULE_CHILDREN}
            onClick={() => onAddGroup(path)}
            type="button"
          >
            Add group
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function LabelFilterEditor({ fields, filter, layerId, onResult }: LabelFilterEditorProps) {
  const setFilter = useAppStore((state) => state.setLayerLabelFilter);
  const usableFields = attributeRuleFields(fields);
  const apply = (next: AttributeRuleGroup | null) => onResult(setFilter(layerId, next));
  const transform = (
    path: AttributeRulePath,
    update: (rule: AttributeRule) => AttributeRule | null,
  ) => {
    if (filter) apply(updateAttributeRuleAtPath(filter, path, update));
  };

  const addCondition = (path: AttributeRulePath) => {
    const condition = createDefaultAttributeRuleCondition(fields);
    if (filter && condition) apply(addAttributeRuleChild(filter, path, condition));
  };
  const addGroup = (path: AttributeRulePath) => {
    const group = createDefaultAttributeRuleGroup(fields);
    if (filter && group) apply(addAttributeRuleChild(filter, path, group));
  };
  const count = filter ? countAttributeRuleConditions(filter) : 0;

  return (
    <details className="label-filter-editor">
      <summary>
        <span>Label filter</span>
        <span>
          {filter ? `${count} ${count === 1 ? 'condition' : 'conditions'}` : 'All features'}
        </span>
      </summary>
      <div className="label-filter-content">
        <p className="labels-placement-note">Label features where</p>
        {filter ? (
          <RuleGroupEditor
            fields={fields}
            group={filter}
            onAddCondition={addCondition}
            onAddGroup={addGroup}
            onChangeCondition={(path, condition) => transform(path, () => condition)}
            onChangeGroup={(path, group) => transform(path, () => group)}
            onRemove={(path) => apply(removeAttributeRuleAtPath(filter, path))}
            path={[]}
          />
        ) : (
          <button
            disabled={usableFields.length === 0}
            onClick={() => apply(createDefaultAttributeRuleGroup(fields))}
            type="button"
          >
            Add condition
          </button>
        )}
        {filter ? (
          <button className="label-filter-clear" onClick={() => apply(null)} type="button">
            Clear filter
          </button>
        ) : null}
      </div>
    </details>
  );
}
